import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { useRFQ } from "../context/RFQContext";
import { useToast } from "../hooks/use-toast";
import { api } from "../api";
import { X, CheckCircle2, Send } from "lucide-react";

export default function RFQModal() {
  const { open, setOpen, enquiry, removeFromEnquiry, clearEnquiry } = useRFQ();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    country: "",
    product: "",
    message: "",
  });

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/rfq", { ...form, items: enquiry });
      setSubmitted(true);
      clearEnquiry();
      toast({ title: "RFQ received", description: "Our sourcing team will get back to you shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: "", company: "", email: "", country: "", product: "", message: "" });
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : resetAndClose())}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0 border-0">
        <div className="grid md:grid-cols-5">
          {/* Brand rail */}
          <div className="hidden md:flex md:col-span-2 brand-gradient text-white p-7 flex-col justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/70">Novocreste</p>
              <h3 className="font-display text-2xl mt-3 leading-tight text-white">
                Request a Quote
              </h3>
              <p className="text-sm text-white/80 mt-3 leading-relaxed">
                Share your requirement and our team will match you with qualified, accredited manufacturing partners.
              </p>
            </div>
            <ul className="space-y-3 mt-8">
              {["Pre-qualified partners", "Regulatory support", "Transparent pricing"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-white/90">
                  <CheckCircle2 className="h-4 w-4 text-[#a9d9e3]" /> {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Form / success */}
          <div className="md:col-span-3 p-6 md:p-7 bg-card">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-8">
                <div className="h-14 w-14 rounded-full bg-[#0f2236] flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-[#2ED9B8]" />
                </div>
                <h3 className="font-display text-2xl mt-4 text-foreground">Thank you</h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                  Your RFQ has been received. Our sourcing team will reach out shortly.
                </p>
                <Button onClick={resetAndClose} className="mt-6 bg-[#00ab96] hover:bg-[#0b8f7e] text-white">
                  Done
                </Button>
              </div>
            ) : (
              <>
                <DialogHeader className="text-left mb-4">
                  <DialogTitle className="font-display text-2xl text-foreground">
                    Request an RFQ
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    Fill in the details below to start your sourcing enquiry.
                  </DialogDescription>
                </DialogHeader>

                {enquiry.length > 0 && (
                  <div className="mb-4 rounded-lg bg-[#0f2236]/60 border border-white/10 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-[#2ED9B8]">
                        Enquiry list ({enquiry.length})
                      </p>
                      <button
                        type="button"
                        onClick={clearEnquiry}
                        className="text-xs text-muted-foreground hover:text-[#2ED9B8]"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {enquiry.map((it) => (
                        <span
                          key={it.slug}
                          className="inline-flex items-center gap-1 rounded-full bg-card border border-white/10 px-2.5 py-1 text-xs text-foreground"
                        >
                          {it.name}
                          <button type="button" onClick={() => removeFromEnquiry(it.slug)}>
                            <X className="h-3 w-3 text-muted-foreground hover:text-[#2ED9B8]" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3.5">
                  <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-xs">Full name</Label>
                      <Input id="name" name="name" required value={form.name} onChange={handleChange} placeholder="Your name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="company" className="text-xs">Company</Label>
                      <Input id="company" name="company" value={form.company} onChange={handleChange} placeholder="Company name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs">Email</Label>
                      <Input id="email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="you@company.com" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="country" className="text-xs">Country</Label>
                      <Input id="country" name="country" value={form.country} onChange={handleChange} placeholder="Country" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="product" className="text-xs">Product / requirement</Label>
                    <Input id="product" name="product" value={form.product} onChange={handleChange} placeholder="e.g. Tablets, Injectables, Nutraceuticals" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="message" className="text-xs">Message</Label>
                    <Textarea id="message" name="message" rows={3} value={form.message} onChange={handleChange} placeholder="Quantities, target markets, specifications..." />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-[#00ab96] hover:bg-[#0b8f7e] text-white h-11">
                    <Send className="h-4 w-4 mr-2" /> {submitting ? "Submitting..." : "Submit RFQ"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
